/** Port of LogViewPieChart.aggregateMacros — daily totals vs profile goals. */
import type { FoodEntry, LogEntry } from '@/types/entries'

export interface DayTotals {
  fats: number
  totalcarbs: number
  netCarbs: number
  proteins: number
  calories: number
  fiber: number
  nutrients: Record<string, number>
  fatGoal: number
  proteinGoal: number
  totalcarbsGoal: number
  caloriesGoal: number
  fatRemaining: number
  fatOver: number | null
  proteinRemaining: number
  proteinOver: number | null
  totalcarbsRemaining: number
  totalcarbsOver: number | null
  netCarbsRemaining: number
  netCarbsOver: number | null
  caloriesRemaining: number
  caloriesOver: number | null
  fatPercentage: number
  proteinPercentage: number
  totalcarbsPercentage: number
  netCarbsPercentage: number
  caloriesPercentage: number
  netCalories: number
  netCaloriesRemaining: number
  netCaloriesOver: number | null
  netCaloriesPercentage: number
  burned: number
}

interface GoalsLike { fat?: number; protein?: number; carb?: number; calories?: number }

export function aggregateMacros(entries: LogEntry[], goals: GoalsLike | undefined, totalBurned: number): DayTotals {
  const g = goals || {}
  let fats = 0, totalcarbs = 0, proteins = 0, calories = 0, fiber = 0
  const nutrients: Record<string, number> = {}
  for (const e of entries) {
    if (e.cat !== 0) continue
    const f = e as FoodEntry
    fats += n(f.fats)
    totalcarbs += n(f.totalcarbs)
    proteins += n(f.proteins)
    calories += n(f.calories)
    fiber += n(f.fiber)
    if (f.nutrients) for (const k of Object.keys(f.nutrients)) nutrients[k] = (nutrients[k] || 0) + n(f.nutrients[k])
  }
  fats = r0(fats); totalcarbs = r0(totalcarbs); proteins = r0(proteins); calories = r0(calories); fiber = r0(fiber)
  for (const k of Object.keys(nutrients)) nutrients[k] = Number(nutrients[k].toFixed(1))
  if (fiber > 0) nutrients['Fiber'] = fiber
  const netCarbs = Math.max(0, r0(totalcarbs - fiber))

  const fatGoal = n(g.fat), proteinGoal = n(g.protein), totalcarbsGoal = n(g.carb), caloriesGoal = n(g.calories)
  const diff = (goal: number, v: number) => ({ remaining: Math.max(goal - v, 0), over: goal - v < 0 ? Math.abs(goal - v) : null })
  const pct = (goal: number, v: number) => Number((goal === 0 ? (v > 0 ? 1 : 0) : v / goal).toFixed(2))
  const fat = diff(fatGoal, fats), protein = diff(proteinGoal, proteins), carbs = diff(totalcarbsGoal, totalcarbs), net = diff(totalcarbsGoal, netCarbs), cal = diff(caloriesGoal, calories)
  const netCalories = calories - totalBurned
  const netCal = diff(caloriesGoal, netCalories)
  return {
    fats, totalcarbs, netCarbs, proteins, calories, fiber, nutrients,
    fatGoal, proteinGoal, totalcarbsGoal, caloriesGoal,
    fatRemaining: fat.remaining, fatOver: fat.over,
    proteinRemaining: protein.remaining, proteinOver: protein.over,
    totalcarbsRemaining: carbs.remaining, totalcarbsOver: carbs.over,
    netCarbsRemaining: net.remaining, netCarbsOver: net.over,
    caloriesRemaining: cal.remaining, caloriesOver: cal.over,
    fatPercentage: pct(fatGoal, fats), proteinPercentage: pct(proteinGoal, proteins),
    totalcarbsPercentage: pct(totalcarbsGoal, totalcarbs), netCarbsPercentage: pct(totalcarbsGoal, netCarbs),
    caloriesPercentage: pct(caloriesGoal, calories),
    netCalories, netCaloriesRemaining: netCal.remaining, netCaloriesOver: netCal.over, netCaloriesPercentage: pct(caloriesGoal, netCalories),
    burned: totalBurned,
  }
}

const n = (v: unknown) => {
  const x = parseFloat(String(v ?? 0))
  return Number.isFinite(x) ? x : 0
}
const r0 = (v: number) => Number(v.toFixed(0))
